import { AlertTriangle, ChevronDown, ChevronUp, Clock, Loader2, PlayCircle, RefreshCw, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { MakeCallModal } from '@/components/common/calls/make-call-modal/MakeCallModal';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCancelLOAChaserMutation, useLazyGetLOAChaserStatusQuery } from '@/services/api/agentflowService';

import { ExecutionHistory, StepTrace } from './ExecutionHistory';
import { ExecutionPlan } from './ExecutionPlan';
import { StepTimeline } from './StepTimeline';
import { TaskActivityFeed } from './TaskActivityFeed';

export interface LOAChaseStatusCardProps {
  /** Matter ID for API calls and artifact links */
  matterId: string;
  /** Coverage ID for API calls */
  coverageId: string;
  /** Task ID to query the workflow by */
  taskId?: string;
  /** Render mode: compact (for CoverageCard) or full (for Ack Stepper) */
  mode?: 'compact' | 'full';
  /** Callback when workflow is not found */
  onWorkflowNotFound?: () => void;
  /** Hide the Task Updates section (use when a separate TaskActivityTimeline is shown) */
  hideTaskUpdates?: boolean;
}

const getStatusLabel = (status: string | undefined): string => {
  switch (status) {
    case 'running':
      return 'Running';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    case 'terminated':
      return 'Terminated';
    case 'not_found':
      return 'Not Started';
    case 'error':
      return 'Error';
    default:
      return status || 'Unknown';
  }
};

const getStatusColor = (status: string | undefined): string => {
  switch (status) {
    case 'running':
      return 'text-violet-600 bg-violet-50';
    case 'completed':
      return 'text-emerald-600 bg-emerald-50';
    case 'failed':
    case 'terminated':
    case 'error':
      return 'text-red-600 bg-red-50';
    default:
      return 'text-zinc-500 bg-zinc-50';
  }
};

export const LOAChaseStatusCard: React.FC<LOAChaseStatusCardProps> = ({
  matterId,
  coverageId,
  taskId,
  mode = 'full',
  onWorkflowNotFound,
  hideTaskUpdates = false,
}) => {
  const [fetchStatus, { data, isFetching }] =
    useLazyGetLOAChaserStatusQuery();
  const [cancelWorkflow, { isLoading: isCancelling }] =
    useCancelLOAChaserMutation();
  const [showCommunications, setShowCommunications] = useState(true);
  const [showTaskUpdates, setShowTaskUpdates] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [callPhoneNumber, setCallPhoneNumber] = useState<string | undefined>();

  const handleMakeCall = (phoneNumber?: string) => {
    setCallPhoneNumber(phoneNumber);
    setIsCallModalOpen(true);
  };

  const handleEnterResults = (stepId: string | number) => {
    // For now, just log - this would open a form to enter BPO results
    console.log('Enter results for step:', stepId);
    // TODO: Implement BPO results entry modal
  };

  useEffect(() => {
    if (matterId && coverageId) {
      fetchStatus({ matterId, coverageId, taskId });
    }
  }, [matterId, coverageId, taskId, fetchStatus]);

  // Notify parent if workflow not found
  useEffect(() => {
    if (data?.workflow_status === 'not_found') {
      onWorkflowNotFound?.();
    }
  }, [data?.workflow_status, onWorkflowNotFound]);

  const handleRefresh = () => {
    fetchStatus({ matterId, coverageId, taskId });
  };

  const handleCancel = async () => {
    if (!taskId) {
      toast.error('Cannot cancel: no task ID');
      return;
    }
    try {
      await cancelWorkflow({
        matterId,
        coverageId,
        request: { task_id: taskId, reason: 'Cancelled by user' },
      }).unwrap();
      toast.success('Workflow cancelled');
      // Refresh to show updated status
      fetchStatus({ matterId, coverageId, taskId });
    } catch (error) {
      toast.error('Failed to cancel workflow');
    }
  };

  // If no workflow or not found, show minimal state with task updates
  if (!data || data.workflow_status === 'not_found') {
    return (
      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        <div className="flex items-center gap-2 text-zinc-500 p-4">
          <Clock className="h-4 w-4" />
          <span className="text-sm font-medium">LOA Chase</span>
          <span className="text-xs text-zinc-400">Not started</span>
        </div>
        {/* Task Updates even when workflow not started */}
        {taskId && !hideTaskUpdates && (
          <div className="border-t border-zinc-100">
            <button
              type="button"
              onClick={() => setShowTaskUpdates(!showTaskUpdates)}
              className="w-full flex items-center justify-between px-4 py-2 hover:bg-zinc-50 transition-colors"
            >
              <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Task Updates
              </span>
              {showTaskUpdates ? (
                <ChevronUp className="h-4 w-4 text-zinc-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-zinc-400" />
              )}
            </button>
            {showTaskUpdates && (
              <div className="px-4 pb-3">
                <TaskActivityFeed
                  matterId={matterId}
                  taskId={taskId}
                  limit={10}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const isRunning = data.workflow_status === 'running';
  const isCompleted = data.workflow_status === 'completed';
  const isFailed = data.workflow_status === 'failed' || data.workflow_status === 'terminated';
  const isError = data.workflow_status === 'error';
  const errorMessage = (data as { error?: string }).error;

  // Convert traces to StepTrace type
  const traces = (data.traces || []) as unknown as StepTrace[];

  if (mode === 'compact') {
    return (
      <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
          <div className="flex items-center gap-2">
            {isRunning && <PlayCircle className="h-4 w-4 text-violet-500" />}
            {isCompleted && <RefreshCw className="h-4 w-4 text-emerald-500" />}
            {(isFailed || isError) && <AlertTriangle className="h-4 w-4 text-red-500" />}
            <span className="text-sm font-medium text-zinc-800">LOA Chase</span>
            <span
              className={cn(
                'px-1.5 py-0.5 text-[10px] font-semibold rounded',
                getStatusColor(data.workflow_status)
              )}
            >
              {getStatusLabel(data.workflow_status)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            {(isRunning || isError) && taskId && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={isCancelling}
                className="p-1 text-red-400 hover:text-red-600 disabled:opacity-50"
                title="Cancel workflow"
              >
                {isCancelling ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <XCircle className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isFetching}
              className="p-1 text-zinc-400 hover:text-zinc-600 disabled:opacity-50"
            >
              {isFetching ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
            </button>
          </div>
        </div>

        {/* Compact timeline */}
        <div className="px-3 py-2">
          {isError && errorMessage ? (
            <div className="text-xs text-red-600 bg-red-50 rounded p-2">
              <span className="font-medium">Workflow error:</span> This workflow encountered an issue and needs to be cancelled and restarted.
            </div>
          ) : data.execution_plan && data.execution_plan.length > 0 ? (
            <StepTimeline steps={data.execution_plan} compact />
          ) : (
            <div className="text-xs text-zinc-500">
              {traces.length} step{traces.length !== 1 ? 's' : ''} executed
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full mode
  return (
    <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-zinc-900">
        <div className="flex items-center gap-2">
          {isRunning && <PlayCircle className="h-4 w-4 text-violet-400" />}
          {isCompleted && <RefreshCw className="h-4 w-4 text-emerald-400" />}
          {(isFailed || isError) && <AlertTriangle className="h-4 w-4 text-red-400" />}
          <span className="text-sm font-semibold text-white">LOA Chase Status</span>
          <span
            className={cn(
              'px-1.5 py-0.5 text-[10px] font-semibold rounded',
              isRunning && 'bg-violet-500/20 text-violet-400',
              isCompleted && 'bg-emerald-500/20 text-emerald-400',
              (isFailed || isError) && 'bg-red-500/20 text-red-400',
              !isRunning && !isCompleted && !isFailed && !isError && 'bg-zinc-700 text-zinc-400'
            )}
          >
            {getStatusLabel(data.workflow_status)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {(isRunning || isError) && taskId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isCancelling}
              className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/20"
            >
              {isCancelling ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <XCircle className="h-3.5 w-3.5 mr-1" />
              )}
              Cancel
            </Button>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isFetching}
            className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50 transition-colors"
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {isError && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-100">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-800">Workflow Error</p>
              <p className="text-xs text-red-700 mt-0.5">
                This workflow encountered an internal error and cannot continue.
                Please cancel and restart the LOA Chase.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {!isError && data.execution_plan && data.execution_plan.length > 0 && (
        <div className="px-4 py-3 border-b border-zinc-100">
          <StepTimeline steps={data.execution_plan} />
        </div>
      )}

      {/* Communications - Collapsible */}
      <div className="border-t border-zinc-100">
        <button
          type="button"
          onClick={() => setShowCommunications(!showCommunications)}
          className="w-full flex items-center justify-between px-4 py-2 hover:bg-zinc-50 transition-colors"
        >
          <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
            Communications ({traces.length})
          </span>
          {showCommunications ? (
            <ChevronUp className="h-4 w-4 text-zinc-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-zinc-400" />
          )}
        </button>
        {showCommunications && (
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-zinc-100">
            {/* Execution History */}
            <div className="px-4 py-3">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                Execution History
              </h3>
              <ExecutionHistory
                traces={traces}
                matterId={matterId}
                onMakeCall={handleMakeCall}
                onEnterResults={handleEnterResults}
              />
            </div>

            {/* Execution Plan */}
            {isRunning && data.execution_plan && data.execution_plan.length > 0 && (
              <div className="px-4 py-3">
                <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
                  Upcoming Steps
                </h3>
                <ExecutionPlan steps={data.execution_plan} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Task Updates - Collapsible */}
      {taskId && !hideTaskUpdates && (
        <div className="border-t border-zinc-100">
          <button
            type="button"
            onClick={() => setShowTaskUpdates(!showTaskUpdates)}
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-zinc-50 transition-colors"
          >
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
              Task Updates
            </span>
            {showTaskUpdates ? (
              <ChevronUp className="h-4 w-4 text-zinc-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-zinc-400" />
            )}
          </button>
          {showTaskUpdates && (
            <div className="px-4 pb-3">
              <TaskActivityFeed
                matterId={matterId}
                taskId={taskId}
                limit={10}
              />
            </div>
          )}
        </div>
      )}

      {/* Footer with timestamps */}
      <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-100 text-[11px] text-zinc-500">
        <div className="flex items-center gap-3">
          {data.start_time && (
            <span>
              Started: {new Date(data.start_time).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </span>
          )}
          {data.close_time && (
            <>
              <span>·</span>
              <span>
                Ended: {new Date(data.close_time).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Make Call Modal */}
      <MakeCallModal
        isOpen={isCallModalOpen}
        onClose={() => {
          setIsCallModalOpen(false);
          setCallPhoneNumber(undefined);
        }}
        initialPhoneNumber={callPhoneNumber}
        matterId={matterId}
        source="loa_chase_status_card"
      />
    </div>
  );
};

export default LOAChaseStatusCard;
